/**
 * Extracts and sanitizes a filename from a URL.
 *
 * Security: Removes path traversal characters and control characters
 * that could be used for header injection attacks.
 *
 * @param url - The URL to extract the filename from
 * @returns A sanitized filename, or 'download' if extraction fails
 */
export function getFilenameFromUrl(url: string): string {
  try {
    const pathname = new URL(url).pathname;
    const rawFilename = pathname.split('/').pop() ?? 'download';
    return sanitizeFilename(rawFilename);
  } catch {
    const rawFilename = url.split('/').pop() ?? 'download';
    return sanitizeFilename(rawFilename);
  }
}

/**
 * Sanitizes a filename to prevent security issues.
 *
 * Removes:
 * - Path traversal characters (/, \, ..)
 * - Control characters (could cause header injection)
 * - Null bytes
 *
 * @param filename - The raw filename to sanitize
 * @returns A safe filename
 */
export function sanitizeFilename(filename: string): string {
  // Remove path traversal and dangerous characters
  let safe = filename
    .replace(/[/\\]/g, '_') // Replace path separators
    .replace(/\.\./g, '_') // Remove directory traversal
    .replace(/[\x00-\x1f\x7f]/g, '') // Remove control characters
    .replace(/["'`]/g, '_') // Remove quotes (header injection)
    .trim();

  // Ensure we have a valid filename
  if (!safe || safe === '.' || safe === '..') {
    safe = 'download';
  }

  // Limit length to prevent issues
  if (safe.length > 255) {
    const ext = safe.lastIndexOf('.');
    if (ext > 0) {
      const extension = safe.slice(ext);
      safe = safe.slice(0, 255 - extension.length) + extension;
    } else {
      safe = safe.slice(0, 255);
    }
  }

  return safe;
}

/**
 * Converts an external file URL to a proxy download URL.
 *
 * This is needed because the `download` attribute on <a> tags
 * doesn't work for cross-origin URLs. The proxy route fetches
 * the file and returns it with the correct Content-Disposition header.
 *
 * @param url - The external file URL
 * @param filename - Optional custom filename for the download
 * @returns The proxy URL that will trigger a proper download
 */
export function getDownloadUrl(url: string, filename?: string): string {
  const params = new URLSearchParams({ url });

  if (filename) {
    params.set('filename', sanitizeFilename(filename));
  }

  return `/api/download?${params.toString()}`;
}
